import React from "react";
import { Link } from "react-router-dom";
import { FileText, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface RecentApplication {
  _id: string;
  projectId: string;
  projectTitle: string;
  projectCategory: string;
  status: string;
  appliedAt: string;
  hourlyRate: number;
}

interface RecentApplicationsProps {
  applications: RecentApplication[];
  loading?: boolean;
}

const RecentApplications: React.FC<RecentApplicationsProps> = ({
  applications,
  loading = false,
}) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "approved":
        return "bg-green-100 text-green-700";
      case "rejected":
        return "bg-red-100 text-red-700";
      case "active":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-36"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-gray-900">Recent Applications</h3>
        </div>
        {/* <Link
          to="/applications"
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          View All <ArrowRight className="w-4 h-4" />
        </Link> */}
      </div>

      {applications.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>No applications yet</p>
          <Link
            to="/projects?tab=available"
            className="text-blue-600 hover:text-blue-700 text-sm mt-2 inline-block"
          >
            Browse available projects
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {applications.slice(0, 3).map((app) => (
            <div
              key={app._id}
              className="flex items-center justify-between p-3 border border-gray-100 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 truncate text-sm">
                  {app.projectTitle}
                </h4>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                  <span>{app.projectCategory}</span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(app.appliedAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
              </div>
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(app.status)}`}
              >
                {app.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentApplications;
